import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import { trackCta } from "@/lib/analytics";

/**
 * Post-Viewer-CTA („Beeindruckt?").
 *
 * Wird nach dem ersten Schließen eines 360°-Viewers PRO SESSION einmalig
 * eingeblendet, sofern der zugehörige Trigger im CMS aktiv ist.
 *
 * Auslöser: globales `CustomEvent("hf:viewer-closed", {detail:{trigger}})`
 * wird von den Modal-Wrappern (`HomeGallery`, `ImmersiveGallery` etc.) beim
 * Modal-Close gefeuert.
 *
 * Session-Flags im `sessionStorage`:
 *  - `hf_pv_cta_seen`   → Card wurde in dieser Session schon angezeigt
 *  - `hf_contact_sent`  → Kontaktformular bereits abgeschickt → nie zeigen
 *
 * Design: dezente Papier-Card mittig unten, max 440px, Fade+Slide-in;
 * bei `prefers-reduced-motion` nur Fade. Auto-Dismiss nach N Sekunden
 * (CMS-konfigurierbar, Default 12s). Escape schließt.
 */

type ViewerTrigger = "photo360" | "video360" | "before_after";
type CmsCta = {
    enabled: boolean;
    headline: string;
    body: string;
    primary_label: string;
    secondary_label: string;
    primary_target_type: "kontakt" | "external";
    primary_target_url: string;
    auto_dismiss_seconds: number;
    trigger_photo360: boolean;
    trigger_video360: boolean;
    trigger_before_after: boolean;
};

const SESSION_SEEN_KEY = "hf_pv_cta_seen";
const CONTACT_SENT_KEY = "hf_contact_sent";

/** Emitter-Helper — wird von den Viewer-Modals beim Close aufgerufen.
 *  Der Import-Punkt ist lokal, damit die Komponente wie ein Event-Bus
 *  wirkt und keine direkte Kopplung zwischen Viewern und Card entsteht. */
export function dispatchViewerClosed(trigger: ViewerTrigger): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
        new CustomEvent("hf:viewer-closed", { detail: { trigger } }),
    );
}

export default function PostViewerCta() {
    const site = useSiteContent();
    const cms = site.getObject<Partial<CmsCta>>("viewer_cta", {});
    const cta: CmsCta = {
        enabled: cms?.enabled !== false,
        headline: (cms?.headline as string) || "Beeindruckt?",
        body: (cms?.body as string) || "Wir dokumentieren auch Ihr Objekt so — unverbindlich, diskret.",
        primary_label: (cms?.primary_label as string) || "Projekt anfragen",
        secondary_label: (cms?.secondary_label as string) || "Weiter schauen",
        primary_target_type: (cms?.primary_target_type as CmsCta["primary_target_type"]) === "external"
            ? "external"
            : "kontakt",
        primary_target_url: (cms?.primary_target_url as string) || "/#kontakt",
        auto_dismiss_seconds: Math.max(3, Math.min(60, Number(cms?.auto_dismiss_seconds) || 12)),
        trigger_photo360: cms?.trigger_photo360 !== false,
        trigger_video360: cms?.trigger_video360 !== false,
        trigger_before_after: cms?.trigger_before_after === true,
    };

    const navigate = useNavigate();
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [trigger, setTrigger] = useState<ViewerTrigger | null>(null);
    const dismissTimer = useRef<number | null>(null);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    const triggerAllowed = useCallback(
        (t: ViewerTrigger): boolean => {
            if (!cta.enabled) return false;
            if (t === "photo360") return cta.trigger_photo360;
            if (t === "video360") return cta.trigger_video360;
            if (t === "before_after") return cta.trigger_before_after;
            return false;
        },
        [cta.enabled, cta.trigger_photo360, cta.trigger_video360, cta.trigger_before_after],
    );

    /** Schließt die Card. `emitDismissed`=false wird nur beim Primary-Click
     *  genutzt (dort loggen wir `cta_clicked`, nicht dismissed). */
    const close = useCallback(
        (emitDismissed: boolean) => {
            if (dismissTimer.current !== null) {
                window.clearTimeout(dismissTimer.current);
                dismissTimer.current = null;
            }
            if (emitDismissed && trigger) {
                trackCta("dismissed", { trigger });
            }
            setVisible(false);
            // Fokus zurück auf den letzten Trigger (falls noch im DOM).
            try {
                previouslyFocused.current?.focus?.();
            } catch {
                /* ignore */
            }
        },
        [trigger],
    );

    const onPrimary = useCallback(() => {
        if (!trigger) return;
        trackCta("clicked", { trigger, target: cta.primary_target_url });
        // Session-Flag setzen, dass die Card gesehen wurde — kein erneutes
        // Auftauchen in derselben Session.
        try { sessionStorage.setItem(SESSION_SEEN_KEY, "1"); } catch { /* ignore */ }
        if (dismissTimer.current !== null) {
            window.clearTimeout(dismissTimer.current);
            dismissTimer.current = null;
        }
        setVisible(false);
        const target = cta.primary_target_url;
        if (cta.primary_target_type === "external" && /^https?:\/\//i.test(target)) {
            window.open(target, "_blank", "noopener,noreferrer");
            return;
        }
        // Kontakt-Anchor: falls schon auf Startseite → smooth scroll,
        // sonst SPA-Navigate.
        if (target.startsWith("/#") || target.startsWith("#")) {
            const hash = target.split("#")[1] || "kontakt";
            if (location.pathname === "/") {
                const el = document.getElementById(hash);
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
                navigate(`/#${hash}`);
            }
        } else {
            navigate(target);
        }
    }, [cta.primary_target_type, cta.primary_target_url, location.pathname, navigate, trigger]);

    // Event-Listener: reagiert auf `hf:viewer-closed` und öffnet die Card,
    // falls Session-Regeln erfüllt sind.
    useEffect(() => {
        const handler = (evt: Event) => {
            const ce = evt as CustomEvent<{ trigger?: ViewerTrigger }>;
            const t = ce.detail?.trigger;
            if (!t || !triggerAllowed(t)) return;
            // Nicht im Admin-Bereich einblenden — dort sind wir bereits
            // eingeloggt und wollen keine Marketing-Card sehen.
            try {
                if (window.location.pathname.startsWith("/admin")) return;
            } catch { /* ignore */ }
            // Anti-Nervig-Guards.
            try {
                if (sessionStorage.getItem(SESSION_SEEN_KEY) === "1") return;
                if (sessionStorage.getItem(CONTACT_SENT_KEY) === "1") return;
            } catch { /* ignore */ }
            previouslyFocused.current = (document.activeElement as HTMLElement) || null;
            setTrigger(t);
            setVisible(true);
            try { sessionStorage.setItem(SESSION_SEEN_KEY, "1"); } catch { /* ignore */ }
            trackCta("shown", { trigger: t });
        };
        window.addEventListener("hf:viewer-closed", handler as EventListener);
        return () => window.removeEventListener("hf:viewer-closed", handler as EventListener);
    }, [triggerAllowed]);

    // Auto-Dismiss + Escape + Fokus-Trap-lite.
    useEffect(() => {
        if (!visible) return;
        // Auto-dismiss.
        dismissTimer.current = window.setTimeout(() => {
            close(true);
        }, cta.auto_dismiss_seconds * 1000);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                close(true);
            }
        };
        window.addEventListener("keydown", onKey);
        // Fokus auf Primary-Button beim Öffnen.
        window.setTimeout(() => {
            const primary = cardRef.current?.querySelector<HTMLButtonElement>(
                '[data-testid="post-viewer-cta-primary"]',
            );
            primary?.focus();
        }, 40);
        return () => {
            window.removeEventListener("keydown", onKey);
            if (dismissTimer.current !== null) {
                window.clearTimeout(dismissTimer.current);
                dismissTimer.current = null;
            }
        };
    }, [visible, cta.auto_dismiss_seconds, close]);

    if (!visible) return null;

    // Reduced-motion: kein Slide, nur Fade.
    const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

    return (
        <div
            className="fixed inset-x-0 z-[80] flex justify-center pointer-events-none px-4"
            style={{ bottom: "24px" }}
            aria-live="polite"
            data-testid="post-viewer-cta-wrap"
        >
            <div
                ref={cardRef}
                role="dialog"
                aria-modal="false"
                aria-labelledby="post-viewer-cta-headline"
                className="pointer-events-auto w-full max-w-[440px] rounded-sm px-5 py-4 relative"
                style={{
                    background: "#FFFDF9",
                    border: "1px solid #D8CDBF",
                    boxShadow: "0 12px 32px -12px rgba(30, 25, 20, 0.28), 0 4px 12px -4px rgba(30, 25, 20, 0.14)",
                    animation: prefersReducedMotion
                        ? "hf-fade-in-only 200ms ease-out"
                        : "hf-cta-slide-in 220ms cubic-bezier(0.16, 1, 0.3, 1)",
                }}
                data-testid="post-viewer-cta-card"
            >
                <button
                    type="button"
                    onClick={() => close(true)}
                    aria-label="Schließen"
                    className="absolute top-2 right-2 p-1 rounded-sm"
                    style={{
                        color: "hsl(var(--hf-muted))",
                        background: "transparent",
                        border: "1px solid transparent",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "hsl(var(--hf-surface))"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    data-testid="post-viewer-cta-close"
                >
                    <X size={14} strokeWidth={2} />
                </button>
                <div
                    id="post-viewer-cta-headline"
                    className="font-display text-lg leading-tight pr-6"
                    style={{ color: "hsl(var(--hf-ink))" }}
                >
                    {cta.headline}
                </div>
                <p
                    className="text-sm mt-1.5 leading-relaxed"
                    style={{ color: "hsl(var(--hf-muted))" }}
                >
                    {cta.body}
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={onPrimary}
                        className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                        style={{
                            background: "hsl(var(--hf-gold-dark))",
                            color: "hsl(var(--hf-bg))",
                            fontWeight: 500,
                            letterSpacing: "0.02em",
                        }}
                        data-testid="post-viewer-cta-primary"
                    >
                        {cta.primary_label}
                        <span aria-hidden style={{ marginLeft: 2 }}>→</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => close(true)}
                        className="text-sm px-3 py-2 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                        style={{
                            background: "transparent",
                            color: "hsl(var(--hf-muted))",
                            border: "1px solid transparent",
                        }}
                        data-testid="post-viewer-cta-secondary"
                    >
                        {cta.secondary_label}
                    </button>
                </div>
            </div>
        </div>
    );
}
